import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { memberSchema } from "@/schemas/member-schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

export type MemberCreateType = z.infer<typeof memberSchema>;

export async function createMemberFn(data: MemberCreateType) {
  return (await axiosInstance.post("/api/members", data)).data;
}

const Member = {
  Create: {
    useMutation: (options = {}) => {
      let toastId: string | number | null = null;
      return useMutation({
        mutationFn: createMemberFn,
        onSuccess: (data) => {
          toastId && toast.dismiss(toastId);
          toast.success("Membership application submitted successfully!");
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error ||
            "Failed to submit membership application";
          toast.error(message);
        },
        onMutate: () => {
          toastId = toast.loading("Submitting application...");
        },
        ...options,
      });
    },
  },

  Get: {
    useQuery: (query: { limit?: number; offset?: number } = {}, options = {}) =>
      useQuery({
        queryKey: ["Members", query],
        queryFn: async () => {
          const params = new URLSearchParams();
          if (query.limit) params.append("limit", query.limit.toString());
          if (query.offset) params.append("offset", query.offset.toString());

          const url = `/api/members${
            params.toString() ? `?${params.toString()}` : ""
          }`;
          return (await axiosInstance.get(url)).data;
        },
        ...options,
      }),
  },

  UpdateStatus: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: async ({ id, status }: { id: string; status: string }) => {
          return (await axiosInstance.patch(`/api/members/${id}`, { status }))
            .data;
        },
        onSuccess: () => {
          toast.success("Member status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["Members"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update member status";
          toast.error(message);
        },
        ...options,
      }),
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: async (id: string) => {
          return (await axiosInstance.delete(`/api/members/${id}`)).data;
        },
        onSuccess: () => {
          toast.success("Member deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Members"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete member";
          toast.error(message);
        },
        ...options,
      }),
  },
};

export default Member;
